import React from 'react';
import {
  alpha,
  Box,
  Button,
  FormControl,
  TextField,
  Typography,
  useTheme,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import ModalDelete from '../ModalDelete';
import BackButton from '../backButton';
import Label from '../Label';

const COUNTRY_CODES = [
  { code: 'MY', name: 'Malaysia' },
  { code: 'OM', name: 'Oman' },
];

// Campaign Details component created to display campaign information
export default function MoreCampaign({
  deleteModal,
  handleCloseMoreCampaigns,
  campaign,
  handleDelete,
  handleOpenDeleteModal,
  handleCloseDeleteModal,
  handleOpenPageCampaigns,
  role,
}) {
  const theme = useTheme();

  // Custom date format: "18 December 2025 1:44PM"
  const formatCustomDateTime = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'dd MMMM yyyy h:mma');
  };

  const buttonStyle = {
    border: '1px solid',
    borderColor: 'grey.300',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    color: theme.palette.grey[700],
    backgroundColor: 'background.paper',
    '&:hover': {
      bgcolor: alpha(theme.palette.grey[500], 0.08),
    },
  };

  const editButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.primary.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.90),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.dark,
      color: 'white',
    },
  };

  const removeButtonStyle = {
    border: 'none',
    px: 2,
    pt: 1,
    pb: 1,
    mr: 2,
    bgcolor: theme.palette.error.main,
    '&:hover': {
      bgcolor: alpha(theme.palette.error.main, 0.85),
    },
    color: 'white',
    fontWeight: 'fontWeightMedium',
    '&.Mui-disabled': {
      bgcolor: theme.palette.primary.dark,
      color: 'white',
    },
  };

  const fontHeaderStyle = {
    color: theme.palette.grey[700],
    fontWeight: 'fontWeightMedium',
    mr: 5,
    minWidth: '250px',
  };

  const formControlStyle = {
    mb: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  };

  //   const getAlertTimeDisplay = (alertTime) => {
  //     if (!alertTime) return 'None';
  //     const options = [];
  //     if (alertTime.onDemand) options.push('On Demand');
  //     if (alertTime.sendNow) options.push('Send Now');
  //     if (alertTime.scheduled) options.push('Scheduled');
  //     return options.length > 0 ? options.join(', ') : 'None';
  //   };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Demand':
        return 'info';
      case 'Scheduled':
        return 'warning';
      case 'Published':
        return 'success';
      case 'Draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCampaignMessageBody = (campaignMessage) => {
    if (typeof campaignMessage === 'object' && campaignMessage !== null) {
      return campaignMessage.body || 'None';
    }
    if (typeof campaignMessage === 'string') {
      return campaignMessage || 'None';
    }
    return 'None';
  };

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <BackButton back={handleCloseMoreCampaigns} type="campaign" />
        <Box sx={
          {
            mt: 1, pb: 5, display: 'flex', flexDirection: 'column', alignItems: 'left',
          }
        }
        >
          <Typography variant="h3">Notifications Campaign</Typography>
          <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>View all details of the campaign</Typography>
        </Box>
        <Box sx={{ padding: 2, borderTop: '1px solid', borderColor: theme.palette.grey[300] }}>
          <Box sx={{ width: '70%', mt: 2 }}>
            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Campaign Name</Typography>
              <TextField disabled sx={{ flexGrow: 1 }} value={campaign?.campaignName || 'None'} />
            </FormControl>

            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Campaign Description</Typography>
              <TextField
                disabled
                sx={{ flexGrow: 1 }}
                value={campaign?.campaignDescription || 'None'}
                multiline
                rows={3}
              />
            </FormControl>

            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Status</Typography>
              <Box sx={
                {
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 'fontWeightLight',
                    fontSize: '14px',
                  },
                }
              }
              >
                {campaign?.status ? (
                  <Label variant="ghost" color={getStatusColor(campaign.status)}>
                    {campaign.status}
                  </Label>
                ) : (
                  <TextField disabled sx={{ flexGrow: 1 }} value="None" />
                )}
              </Box>
            </FormControl>

            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Target Audience</Typography>
              <Box sx={{
                flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1,
              }}
              >
                {(() => {
                  // Check if targetAudience contains country codes
                  const countryCodeValues = COUNTRY_CODES.map((c) => c.code);
                  const targetAudienceValue = campaign?.targetAudience || 'None';
                  const isCountryCode = targetAudienceValue !== 'Everyone'
                    && targetAudienceValue !== 'None'
                    && (countryCodeValues.includes(targetAudienceValue)
                        || targetAudienceValue.split(',').some((code) => countryCodeValues.includes(code.trim())));

                  // Get single country code from targetAudience or selectedCountryCodes
                  let countryCode = null;
                  if (isCountryCode) {
                    if (campaign?.selectedCountryCodes
                      && campaign.selectedCountryCodes.length > 0) {
                      const [firstCode] = campaign.selectedCountryCodes;
                      countryCode = firstCode;
                    } else if (typeof targetAudienceValue === 'string') {
                      const codes = targetAudienceValue.split(',')
                        .map((code) => code.trim())
                        .filter((code) => countryCodeValues.includes(code));
                      const [firstCode] = codes;
                      countryCode = firstCode || targetAudienceValue;
                    } else if (countryCodeValues.includes(targetAudienceValue)) {
                      countryCode = targetAudienceValue;
                    }
                  }

                  return (
                    <>
                      <TextField
                        disabled
                        sx={{ flexGrow: 1 }}
                        value={isCountryCode ? 'Country Code' : targetAudienceValue}
                      />
                      {isCountryCode && countryCode && (
                        <Box sx={{
                          display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1,
                        }}
                        >
                          {(() => {
                            const country = COUNTRY_CODES.find((c) => c.code === countryCode);
                            return (
                              <Chip
                                key={countryCode}
                                label={country ? `${country.code} - ${country.name}` : countryCode}
                                size="small"
                                sx={{
                                  color: theme.palette.grey[700],
                                  fontSize: '12px',
                                  fontWeight: 'fontWeightBold',
                                }}
                              />
                            );
                          })()}
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
            </FormControl>

            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Campaign Message</Typography>
              <TextField
                disabled
                sx={{ flexGrow: 1 }}
                value={getCampaignMessageBody(campaign?.campaignMessage)}
                multiline
                rows={4}
              />
            </FormControl>

            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Last Triggered</Typography>
              <TextField
                disabled
                sx={{ flexGrow: 1 }}
                value={formatCustomDateTime(campaign?.lastTriggered)}
              />
            </FormControl>

            <FormControl sx={formControlStyle}>
              <Typography sx={fontHeaderStyle}>Created At</Typography>
              <TextField
                disabled
                sx={{ flexGrow: 1 }}
                value={formatCustomDateTime(campaign?.createdAt)}
              />
            </FormControl>
          </Box>
        </Box>
        <Box sx={
          {
            display: 'flex',
            justifyContent: 'right',
            borderTop: '1px solid',
            borderColor: theme.palette.grey[300],
            pt: 3,
          }
        }
        >
          <Button sx={buttonStyle} onClick={() => handleCloseMoreCampaigns()}>
            <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
              Cancel
            </Typography>
          </Button>
          {(role === 900 || role === 101 || role === 111) && (
            <>
              <Button sx={removeButtonStyle} onClick={() => handleOpenDeleteModal()}>
                <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
                  Remove Campaign
                </Typography>
              </Button>
              {campaign?.status !== 'Published' && (
                <Button sx={editButtonStyle} onClick={() => handleOpenPageCampaigns(campaign)}>
                  <Typography sx={{ fontWeight: 'fontWeightMedium' }}>
                    Edit
                  </Typography>
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
      {deleteModal && (
      <ModalDelete
        deleteModal={deleteModal}
        handleDelete={handleDelete}
        handleClose={handleCloseDeleteModal}
        data={campaign}
      />
      )}
    </>
  );
}
